import axios from 'axios';
import { useEffect, useState } from 'react';

// API lives on the same server that serves this UI (backend on :8787 serves both).
// For `npm run dev`, point it at the backend with VITE_API_BASE=http://192.168.0.6:8787/api/
export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api/';

// Reusable Base Axios instance configured with the Crowd Detection API
export const baseapi = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Strip leading slash so both baseapi.get('/endpoint') and baseapi.get('endpoint') resolve correctly under /api/
baseapi.interceptors.request.use((config) => {
  if (config.url && config.url.startsWith('/')) {
    config.url = config.url.replace(/^\/+/, '');
  }
  return config;
});

// ---- outage tracking
// A backend restart takes from a few seconds to a minute and a half. During that time requests fail, but the
// screens keep their last figures and say nothing: a message would only alarm the control room. Only when no
// request has succeeded for OUTAGE_GRACE_MS do the screens report that the server cannot be reached.
export const OUTAGE_GRACE_MS = 120000;
let failingSince: number | null = null;
baseapi.interceptors.response.use(
  (res) => { failingSince = null; return res; },
  (err) => {
    const status = err?.response?.status;
    // no answer at all, or the web server answering for a backend that is down (502 / 503 / 504)
    if (!err?.response || status === 502 || status === 503 || status === 504) { if (failingSince === null) failingSince = Date.now(); }
    else failingSince = null;                    // the backend answered (even with an error): it is up
    return Promise.reject(err);
  },
);
/** True once the API has been failing for longer than a restart takes. */
export const apiOutage = (): boolean => failingSince !== null && Date.now() - failingSince > OUTAGE_GRACE_MS;
/** apiOutage() as React state, re-checked every 5 s. */
export function useApiOutage(): boolean {
  const [down, setDown] = useState<boolean>(apiOutage());
  useEffect(() => {
    const t = window.setInterval(() => setDown(apiOutage()), 5000);
    return () => window.clearInterval(t);
  }, []);
  return down;
}

export const baseApi = baseapi;
export default baseapi;
